/**
 * Bootstrap: create the Pixi application sized to the viewport, then run a
 * scene switcher between gameplay (GameScene) and the level editor
 * (EditorScene, dynamically imported so it doesn't bloat the gameplay bundle).
 */

import { Application } from 'pixi.js';
import { GameScene, type GameSceneOptions } from './scenes/GameScene.js';
import type { LevelData } from './gameplay/LevelLoader.js';
import { parseShareLink, type SharePayload } from './gameplay/ShareLink.js';

type Scene = { destroy(): void };

async function main(): Promise<void> {
  const host = document.getElementById('app');
  if (!host) throw new Error('#app element missing');

  const app = new Application();
  await app.init({
    background: '#1b0f30',
    resizeTo: window,
    antialias: true,
    autoDensity: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
  });

  host.appendChild(app.canvas);

  let current: Scene | null = null;
  /** Latest level the user is editing. Persisted across test-mode round trips
   *  so "Return to Editor" lands back in the same draft. */
  let editorLevel: LevelData | null = null;

  const goGame = () => {
    if (current) { current.destroy(); current = null; }
    editorLevel = null;
    current = new GameScene(app, {
      onEnterEditor: (level) => { void goEditor(level); },
    });
  };

  /** Boot directly into a deterministic replay (from a share URL). The
   *  receiver lands in the regular GameScene with the run already playing,
   *  so they can keep playing past the win overlay if they want. */
  const goReplay = (payload: SharePayload) => {
    if (current) { current.destroy(); current = null; }
    editorLevel = null;
    const opts: GameSceneOptions = {
      replay: payload.level
        ? { replay: payload.replay, level: payload.level }
        : { replay: payload.replay },
      ...(payload.track ? { track: payload.track } : {}),
    };
    current = new GameScene(app, {
      onEnterEditor: (level) => { void goEditor(level); },
    }, opts);
  };

  const goEditor = async (level: LevelData) => {
    if (current) { current.destroy(); current = null; }
    editorLevel = level;
    const { EditorScene } = await import('./editor/EditorScene.js');
    current = new EditorScene(app, level, {
      onExitEditor: () => goGame(),
      onTestLevel: (lvl) => { editorLevel = lvl; goTest(lvl); },
    });
  };

  const goTest = (level: LevelData) => {
    if (current) { current.destroy(); current = null; }
    current = new GameScene(app, {
      onExitTest: () => {
        if (editorLevel) void goEditor(editorLevel);
        else goGame();
      },
    }, { testLevel: level });
  };

  // If the URL carries a share link, parse it and boot straight into the
  // replay. Strip the params from history so a reload doesn't loop the
  // user back into playback. On any parse error we fall back to the
  // normal title screen and log so dev tools surface the issue.
  let bootedFromShare = false;
  if (window.location.search.includes('r=')) {
    try {
      const payload = await parseShareLink(window.location.search);
      if (payload) {
        history.replaceState(null, '', window.location.pathname);
        goReplay(payload);
        bootedFromShare = true;
      }
    } catch (e) {
      console.warn('Share link parse failed:', e);
    }
  }
  if (!bootedFromShare) goGame();

  // Register the service worker generated by vite-plugin-pwa so the game
  // becomes installable & playable offline. autoUpdate handles new versions
  // silently — the next reload picks them up.
  if (import.meta.env.PROD) {
    void import('virtual:pwa-register').then(({ registerSW }) => {
      registerSW({ immediate: true });
    }).catch((e) => { console.warn('SW register failed', e); });
  }
}

main().catch((err) => {
  console.error(err);
  const host = document.getElementById('app');
  if (host) host.textContent = 'Failed to start: ' + (err instanceof Error ? err.message : String(err));
});
